import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import type { Insert, Update } from "@/types";

export function useClients(opts: { search?: string; status?: string } = {}) {
  return useQuery({
    queryKey: ["clients", opts],
    queryFn: async () => {
      let q = supabase.from("clients").select("*").is("deleted_at", null).order("legal_name");
      if (opts.status && opts.status !== "all") q = q.eq("status", opts.status as never);
      if (opts.search?.trim()) q = q.or(`legal_name.ilike.%${opts.search.trim()}%,trade_name.ilike.%${opts.search.trim()}%,billing_email.ilike.%${opts.search.trim()}%`);
      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: ["client", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await supabase.from("clients").select("*, tax_regimes(label, rate_pct, mention)").eq("id", id!).single();
      if (error) throw error;
      return data;
    },
  });
}

export function useSaveClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id?: string; values: Insert<"clients"> | Update<"clients"> }) => {
      if (input.id) {
        const { data, error } = await supabase.from("clients").update(input.values as Update<"clients">).eq("id", input.id).select().single();
        if (error) throw error;
        return data;
      }
      const { data, error } = await supabase.from("clients").insert(input.values as Insert<"clients">).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data, input) => {
      void qc.invalidateQueries({ queryKey: ["clients"] });
      void qc.invalidateQueries({ queryKey: ["client", data.id] });
      toast.success(input.id ? "Client mis à jour" : "Client créé");
      logger.info(input.id ? "client.updated" : "client.created", { client_id: data.id });
    },
    onError: (e) => { toast.error("Enregistrement impossible."); logger.error("client.save", e); },
  });
}

export function useContacts(clientId: string | undefined) {
  return useQuery({
    queryKey: ["contacts", clientId],
    enabled: Boolean(clientId),
    queryFn: async () => {
      const { data, error } = await supabase.from("contacts").select("*").eq("client_id", clientId!).order("is_primary", { ascending: false }).order("last_name");
      if (error) throw error;
      return data;
    },
  });
}

export function useSaveContact(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id?: string; values: Omit<Insert<"contacts">, "client_id"> }) => {
      const values = { ...input.values, client_id: clientId };
      const q = input.id ? supabase.from("contacts").update(values).eq("id", input.id) : supabase.from("contacts").insert(values);
      const { error } = await q;
      if (error) throw error;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["contacts", clientId] }); toast.success("Interlocuteur enregistré"); },
    onError: (e) => {
      const code = (e as { code?: string }).code;
      toast.error(code === "23505" ? "Il y a déjà un interlocuteur principal pour ce client." : "Enregistrement impossible.");
      logger.error("contact.save", e);
    },
  });
}


export function useClientPrices(clientId: string | undefined) {
  return useQuery({
    queryKey: ["client_prices", clientId],
    enabled: Boolean(clientId),
    queryFn: async () => {
      const { data, error } = await supabase.from("client_prices").select("*, products(sku, name, unit_price_eur, unit_price_usd)")
        .eq("client_id", clientId!).order("valid_from", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function useSaveClientPrice(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (values: Omit<Insert<"client_prices">, "client_id">) => {
      const { error } = await supabase.from("client_prices").insert({ ...values, client_id: clientId });
      if (error) throw error;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["client_prices", clientId] }); toast.success("Tarif négocié enregistré"); },
    onError: (e) => { toast.error("Enregistrement impossible."); logger.error("client_price.save", e); },
  });
}


export function useActivities(clientId: string | undefined) {
  return useQuery({
    queryKey: ["activities", clientId],
    enabled: Boolean(clientId),
    queryFn: async () => {
      const { data, error } = await supabase.from("activities").select("*, profiles!activities_created_by_profile_fk(full_name, email)")
        .eq("client_id", clientId!).order("occurred_at", { ascending: false }).limit(200);
      if (error) throw error;
      return data;
    },
  });
}

export function useAddActivity(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (values: Omit<Insert<"activities">, "client_id">) => {
      const { error } = await supabase.from("activities").insert({ ...values, client_id: clientId });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["activities", clientId] });
      void qc.invalidateQueries({ queryKey: ["client", clientId] });
      void qc.invalidateQueries({ queryKey: ["clients"] });
    },
    onError: (e) => { toast.error("Enregistrement impossible."); logger.error("activity.add", e); },
  });
}

export function useTasks(clientId: string | undefined) {
  return useQuery({
    queryKey: ["tasks", clientId],
    enabled: Boolean(clientId),
    queryFn: async () => {
      const { data, error } = await supabase.from("tasks").select("*").eq("client_id", clientId!).order("completed_at", { nullsFirst: true }).order("due_at");
      if (error) throw error;
      return data;
    },
  });
}

export function useSaveTask(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id?: string; values: Partial<Insert<"tasks">> }) => {
      const q = input.id
        ? supabase.from("tasks").update(input.values).eq("id", input.id)
        : supabase.from("tasks").insert({ ...(input.values as Insert<"tasks">), client_id: clientId });
      const { error } = await q;
      if (error) throw error;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["tasks", clientId] }); },
    onError: (e) => { toast.error("Enregistrement impossible."); logger.error("task.save", e); },
  });
}
